import {
  experienciaStatusPadraoContaComoAtiva,
  tipoStatusProgramadoDominanteNoDia,
  ymdReferenciaAgendaStatusLocal,
  type ExperienciaStatusPadraoValor,
} from '../../../shared/lib/statusProgramadoNoticia';
import type { EstabelecimentoResponsavel } from './estabelecimentosStorage';

/**
 * Storage de categorias, subcategorias e experiências (admin).
 * Persistido em localStorage.
 * Não usa defaultCidades nem defaultSubcategorias — apenas dados do localStorage.
 */

export interface Categoria {
  id: string;
  name: string;
}

export interface Subcategoria {
  id: string;
  name: string;
  /** Uma única categoria pai. */
  categoryId: string;
  /** Ordem de exibição dentro da categoria (0 = primeiro). Sincroniza com o catálogo no site. */
  sortOrder: number;
}

export interface DificuldadePercentuais {
  leve: number;
  facil: number;
  moderado: number;
  dificil: number;
  desafiador: number;
}

export interface PremiacaoPeriodo {
  primeiro?: number;
  segundo?: number;
  terceiro?: number;
}

export interface PremiacaoConfig {
  /** @deprecated Use temporadaChuva */
  mensal?: PremiacaoPeriodo;
  anual?: PremiacaoPeriodo;
  /** Temporada Chuva (out – abr) */
  temporadaChuva?: PremiacaoPeriodo;
  /** Temporada Seca (abr – out) */
  temporadaSeca?: PremiacaoPeriodo;
}

/** Chaves dos dias da semana para horário de funcionamento (passeios). */
export type DiaSemanaKeyExperiencia = 'seg' | 'ter' | 'qua' | 'qui' | 'sex' | 'sab' | 'dom';

export interface HorarioDiaExperiencia {
  fechado: boolean;
  /** HH:mm ou vazio */
  inicio: string;
  /** HH:mm ou vazio */
  fim: string;
}

export type HorarioFuncionamentoSemana = Record<DiaSemanaKeyExperiencia, HorarioDiaExperiencia>;

/** Padrão para nova experiência: todos os dias abertos, 08:00–17:00. */
export function defaultHorarioFuncionamento(): HorarioFuncionamentoSemana {
  const dia = (): HorarioDiaExperiencia => ({ fechado: false, inicio: '08:00', fim: '17:00' });
  return { seg: dia(), ter: dia(), qua: dia(), qui: dia(), sex: dia(), sab: dia(), dom: dia() };
}

export function mergeHorarioFuncionamento(raw?: Partial<HorarioFuncionamentoSemana> | null): HorarioFuncionamentoSemana {
  const base = defaultHorarioFuncionamento();
  if (!raw || typeof raw !== 'object') return base;
  const keys: DiaSemanaKeyExperiencia[] = ['seg', 'ter', 'qua', 'qui', 'sex', 'sab', 'dom'];
  for (const k of keys) {
    const v = raw[k];
    if (v && typeof v === 'object') {
      base[k] = {
        fechado: Boolean(v.fechado),
        inicio: typeof v.inicio === 'string' ? v.inicio : '',
        fim: typeof v.fim === 'string' ? v.fim : '',
      };
    }
  }
  return base;
}

/** Dia (ou feriado anual) em que o passeio não funciona, além do horário semanal. */
export interface FechamentoCalendarioExperiencia {
  id: string;
  /** Data fixa `YYYY-MM-DD` ou feriado anual `MM-DD` quando repetirCadaAno=true */
  data: string;
  repetirCadaAno?: boolean;
  motivo?: string;
}

export function normalizeFechamentosCalendario(raw?: unknown): FechamentoCalendarioExperiencia[] {
  if (!Array.isArray(raw)) return [];
  const out: FechamentoCalendarioExperiencia[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const data = typeof r.data === 'string' ? r.data.trim() : '';
    if (!data) continue;
    const id =
      typeof r.id === 'string' && r.id.trim()
        ? r.id.trim()
        : `fech-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    out.push({
      id,
      data,
      repetirCadaAno: Boolean(r.repetirCadaAno),
      motivo: typeof r.motivo === 'string' ? r.motivo.trim() || undefined : undefined,
    });
  }
  return out;
}

/** Retorna o fechamento que cobre o dia civil `YYYY-MM-DD` (data fixa ou MM-DD anual). */
export function findFechamentoCalendarioNoDia(
  isoDia: string,
  list: FechamentoCalendarioExperiencia[] | undefined
): FechamentoCalendarioExperiencia | undefined {
  if (!list?.length || !/^\d{4}-\d{2}-\d{2}$/.test(isoDia)) return undefined;
  const md = isoDia.slice(5);
  return list.find((f) => {
    if (f.repetirCadaAno) return f.data === md;
    return f.data === isoDia;
  });
}

/** Opções de transporte por cidade de embarque: define quais veículos podem fazer translado partindo da cidade. */
export interface TransportOpcoes {
  passeio?: boolean;
  suv4x4?: boolean;
  taxi?: boolean;
  suv7lugares?: boolean;
  van16?: boolean;
  van22?: boolean;
}

export interface Cidade {
  id: string;
  pais: string;
  estadoProvincia: string;
  /** Uma cidade pode estar em múltiplas regiões */
  regioes: string[];
  cep: string;
  cidade: string;
  /** Coordenadas para filtro por GPS (cidades próximas ao guia) */
  lat?: number;
  lng?: number;
  /** Fotos da cidade (até 4). Referência s3:chave (nuvem), URL https ou data URL legado. Mínimo 1 ao cadastrar. */
  fotos?: string[];
  /** Índice da foto principal (0 a 3). Default 0. */
  fotoPrincipalIndex?: number;
  /** Opções de transporte disponíveis para translado partindo desta cidade */
  transportOpcoes?: TransportOpcoes;
}

/** Uma linha de tarifa: valor em R$ e USD (e opcionalmente diária guia / % comissão bilíngue para passeio exclusivo) */
export interface TarifarioRowItem {
  brl?: number;
  usd?: number;
  /** Diária guia (passeio exclusivo) */
  diariaGuiaBrl?: number;
  diariaGuiaUsd?: number;
  /** % taxa comissão bilíngue (passeio exclusivo) */
  comissaoPctBilingue?: number;
  /** % comissão agência (transporte) — diariaGuia = brl * (1 - comissaoPct/100) */
  comissaoPct?: number;
}

export interface Tarifario {
  id: string;
  nome: string;
  descricao?: string;
  /** Taxa agência (%) */
  taxaGeralAgencia?: number;
  /** Taxa mínima (%) */
  taxaMinimaAgencia?: number;
  /** Taxa máxima (%) */
  taxaMaximaAgencia?: number;
  // Excursões
  excursaoIndividual?: TarifarioRowItem;
  excursaoIndividualTranslado?: TarifarioRowItem;
  // Veículos / Transporte
  veiculo4x4_6?: TarifarioRowItem;
  veiculo4x4_4?: TarifarioRowItem;
  veiculoPasseio?: TarifarioRowItem;
  /** Van 16 lugares */
  van15?: TarifarioRowItem;
  /** Van 22 lugares */
  van20?: TarifarioRowItem;
  /** Táxi */
  taxi?: TarifarioRowItem;
  // Deslocamento do guia
  deslocamentoMoto?: TarifarioRowItem;
  deslocamentoCarro?: TarifarioRowItem;
  // Passeio exclusivo: até 1..12 pessoas (12 itens)
  passeioExclusivo?: TarifarioRowItem[];
  // Descontos e extras
  descontoIdoso?: number;
  descontoAdolescenteCrianca?: number;
  descontoCrianca?: number;
  descontoAdolescente?: number;
  idadeMaxCriancaNaoPaga?: number;
  descontoBebe?: number;
  aditivoGuiaBilingue?: number;
}

export interface PontoEncontro {
  id: string;
  cidadeId: string;
  nome: string;
  endereco?: string;
  cep?: string;
  /** Ponto padrão da cidade */
  padrao?: boolean;
}

export interface CidadeEmbarque {
  cidadeId: string;
  /** Nome da cidade (persistido para o cliente listar destinos mesmo sem acesso ao storage de cidades). */
  cidadeNome?: string;
  /** Obrigatório para passeios. Opcional para terapia (cidades que praticam a terapia). */
  tarifarioId?: string;
  /** Distância em quilômetros */
  distanciaKm?: number;
  /** Tempo em minutos (legado; não exibido no admin) */
  tempoMinutos?: number;
  /** Trecho em estrada de terra (legado; substituído por estradaTerraKm) */
  estradaTerra?: boolean;
  /** Estrada de terra em km */
  estradaTerraKm?: number;
  /** Duração do passeio a partir desta cidade (ex.: "5h"). Independente por cidade. */
  duracaoPasseio?: string;
  /** @deprecated Transporte agora é configurado na Cidade. Mantido para compatibilidade com dados antigos. */
  transportOpcoes?: TransportOpcoes;
}

/** Relação "Faça no mesmo dia" / "Combinada com": até 3 experiências da mesma região + tarifário */
/** Grupos de idade da viagem (passageiros) */
export type GrupoIdade = 'seniors' | 'adults' | 'adolescents' | 'children' | 'babies';

/** Período de validade: sempre válido ou período com datas (YYYY-MM-DD) */
export type ValidadePeriodo =
  | { sempre?: true }
  | { periodoDe: string; periodoAte: string };

/** Desconto variável por grupo de idade. Grupos não podem estar em meia entrada. */
export interface DescontoIngresso {
  id?: string;
  tipo: 'percentual' | 'valor_fixo';
  valor: number;
  /** Grupos que recebem este desconto (excluir grupos em meiaEntradaGrupos) */
  grupos: GrupoIdade[];
  /** Validade do desconto. Se omitido, válido sempre. */
  validade?: ValidadePeriodo;
}

/** Tipo de ingresso para experiências (moeda por tipo) */
export interface TicketTypeExperiencia {
  id: string;
  name: string;
  price: number;
  quantity: number;
  /** Moeda do tipo (BRL ou USD) */
  currency?: string;
  /** Período de validade do ingresso. Se omitido, válido sempre. */
  validade?: ValidadePeriodo;
  /** Grupos que têm direito a meia entrada (50% off) neste ingresso */
  meiaEntradaGrupos?: GrupoIdade[];
  /** Período de validade da meia entrada. Se omitido, válido sempre. */
  meiaValidade?: ValidadePeriodo;
  /** Múltiplos descontos variáveis (R$ ou %) para grupos específicos. Não pode haver grupo em comum com meiaEntradaGrupos. */
  descontos?: DescontoIngresso[];
  /** @deprecated Use descontos. Mantido para compatibilidade. */
  desconto?: DescontoIngresso;
}

export interface RelacionadoRow {
  experienciaIds: string[];
  /** Tarifário aplicável a esta combinação */
  tarifarioId?: string;
  aumentoPercentual?: number;
  /** Duração do passeio desta combinação (ex.: "2h") */
  duracaoPasseio?: string;
  /** IDs das cidades de embarque para esta combinação (mínimo 1). As cidades são as do cidadesEmbarque da experiência. */
  cidadesEmbarqueIds?: string[];
}

/** Tipos de período em «Status programado» (admin), alinhados ao painel de Estabelecimentos / notícias. */
export type ExperienciaPeriodoStatusTipo =
  | 'ativada'
  | 'desativada'
  | 'naviex'
  | 'principal'
  | 'categoria';

export interface ExperienciaAdmin {
  id: string;
  titulo: string;
  /** CNPJ opcional (exibição / parceiros). */
  cnpj?: string;
  subtitulo?: string;
  /** Resumo curto (entre subtítulo e descrição) */
  resumo?: string;
  categoryIds: string[];
  subcategoryIds: string[];
  /** Tags (ex.: Cachoeira, Praia de rio) */
  tags?: string[];
  /** Responsáveis comerciais (mesmo modelo que estabelecimentos). */
  responsaveis?: EstabelecimentoResponsavel[];
  descricao?: string;
  createdAt: string;
  updatedAt: string;
  status?: 'rascunho' | 'publicado';
  petFriendly?: boolean;
  /** Se true, apenas veículos 4x4 (SUV 5 e 7 lugares) podem fazer transporte para esta atração */
  somenteVeiculos4x4?: boolean;
  /**
   * Se true, não aparece nos catálogos principais (cliente, guia, excursão admin).
   * Só entra em fluxo "Faça no mesmo dia" / combinada quando ligada a uma atividade principal.
   */
  naoEhAtividadePrincipal?: boolean;
  /** Preenchido quando categoria Natureza ou City Tour */
  dificuldadePercentuais?: DificuldadePercentuais;
  duracaoPasseio?: string;
  caminhadaKm?: number;
  /** @deprecated Use isFree + ticketTypes */
  ingressoPorPessoa?: number;
  /** @deprecated Use ticketTypes */
  meiaEntrada?: boolean;
  /** Entrada gratuita (sem tipos de ingresso pagos) */
  isFree?: boolean;
  /** Tipos de ingresso (quando isFree=false) */
  ticketTypes?: TicketTypeExperiencia[];
  /** Máximo de participantes por guia */
  maxParticipantesPorGuia?: number;
  cidadeId?: string;
  /** Nome da cidade (para exibição no catálogo/cliente quando mesma origem) */
  cidadeNome?: string;
  /** Coordenadas GPS da localização da experiência (para filtro e cidades próximas) */
  localizacaoLat?: number;
  localizacaoLng?: number;
  /** Link do Google Maps ou texto lat,lng exatamente como colado no admin (reabre o formulário com o mesmo valor). */
  localizacaoMapsLink?: string;
  pontoEncontroId?: string;
  cidadesEmbarque?: CidadeEmbarque[];
  /** Experiências que podem ser feitas no mesmo dia (Faça no mesmo dia / Combinada com) */
  relacionados?: RelacionadoRow[];
  /** URLs ou data URLs das imagens de destaque (legado). Use imagensSEO quando disponível. */
  imagens?: string[];
  /** Imagens com orientação e SEO (vertical 9:16, horizontal 16:9). Quando preenchido, tem prioridade sobre imagens. */
  imagensSEO?: Array<{ url: string; orientacao: 'vertical' | 'horizontal'; alt?: string; title?: string }>;
  /** Status padrão quando não há período específico (inclui destaques fora do calendário). */
  statusPadrao?: ExperienciaStatusPadraoValor;
  /** Períodos de ativação/desativação e destaques programados. */
  periodosStatus?: { de: string; ate: string; tipo: ExperienciaPeriodoStatusTipo | string }[];
  /** Histórico de alterações permanentes por dia (mesma semântica que estabelecimentos). */
  statusProgramadoHistorico?: {
    quando: string;
    dia: string;
    tipo: string;
    modo?: 'periodo' | 'permanente';
    ate?: string;
  }[];
  /** @deprecated Use statusPadrao + periodosStatus */
  ativo?: boolean;
  /** @deprecated */
  periodoAtivoDe?: string;
  /** @deprecated */
  periodoAtivoAte?: string;
  /** Melhor período para visitar: meses (1-12) */
  melhorPeriodoMeses?: number[];
  /** Premiação opcional para ranking por atrativo (mensal/anual: 1º,2º,3º). */
  premiacaoRanking?: PremiacaoConfig;
  /** Tipo: passeio (padrão) ou terapia */
  tipoExperiencia?: 'passeio' | 'terapia';
  /** Tempo da sessão em horas (terapia) */
  tempoSessaoHoras?: number;
  /** Tempo da sessão em minutos: 0, 10, 15, 20, 30, 40, 45, 50 (terapia) */
  tempoSessaoMinutos?: number;
  /** @deprecated Use valorSessao[].modalidades. Mantido para compatibilidade. */
  localizacaoTerapia?: ('presencial' | 'domicilio' | 'distancia')[];
  /** Endereço para atendimento presencial (opcional). */
  enderecoPresencial?: string;
  /** Nome da localização (ex.: Consultório Centro) */
  nomeLocalizacao?: string;
  /** Como chegar (instruções) */
  comoChegar?: string;
  /** IDs de estabelecimentos associados à localização (0 ou mais) */
  estabelecimentoIds?: string[];
  /** Valor da sessão (terapia): nome, preço, moeda, modalidade. Mínimo 1 valor. Cada item é uma opção; modalidade opcional (presencial, domicílio ou distância — só uma por item). */
  valorSessao?: { nome: string; preco: number; moeda: string; modalidade?: 'presencial' | 'domicilio' | 'distancia' }[];
  /** Horário de funcionamento por dia (passeios). Persistido e enviado no sync admin → AWS. */
  horarioFuncionamento?: HorarioFuncionamentoSemana;
  /** Datas especiais / feriados sem funcionamento (calendário). */
  fechamentosCalendario?: FechamentoCalendarioExperiencia[];
}

const KEY_CATEGORIAS = 'naviex-admin-categorias';
const KEY_CIDADES = 'naviex-admin-cidades';
const KEY_SUBCATEGORIAS = 'naviex-admin-subcategorias';
const KEY_REGIOES = 'naviex-admin-regioes';
/** Chave localStorage usada pelo admin. Cliente e guia (mesma origem) podem ler para listar experiências publicadas. */
export const KEY_EXPERIENCIAS = 'naviex-admin-experiencias';
const KEY_TARIFARIOS = 'naviex-admin-tarifarios';
const KEY_TARIFARIO_GLOBAL = 'naviex-admin-tarifario-global';
const KEY_PONTOS_ENCONTRO = 'naviex-admin-pontos-encontro';

/** Região associada a um ou mais estados. Usada para listar regiões ao selecionar estado no cadastro de cidade. */
export interface Regiao {
  nome: string;
  estados: string[];
}

/** Configurações globais do tarifário (taxas agência, descontos, idade máx. criança). */
export interface TarifarioGlobalConfig {
  taxaGeralAgencia?: number;
  taxaMinimaAgencia?: number;
  taxaMaximaAgencia?: number;
  descontoIdoso?: number;
  descontoAdolescenteCrianca?: number;
  /** Desconto (%) para crianças (3 a 12 anos). */
  descontoCrianca?: number;
  /** Desconto (%) para adolescentes (13 a 17 anos). */
  descontoAdolescente?: number;
  /** Desconto (%) aplicado em passeios compartilhados (ex: 30 = 30% de desconto). Usado no checkout do cliente. */
  descontoGuia?: number;
  /** @deprecated Use descontoGuia. Mantido para compatibilidade com dados antigos. */
  descontoCompartilhado?: number;
  /** Desconto (%) aplicado em veículos. */
  descontoVeiculo?: number;
  /** @deprecated Use descontoBebe. Mantido para compatibilidade. */
  idadeMaxCriancaNaoPaga?: number;
  /** Desconto (%) para bebês (0 a 2 anos). */
  descontoBebe?: number;
  /** Taxa de serviço (%) aplicada sobre ingressos/entradas incluídas. */
  taxaServicoIngresso?: number;
  /** Piso Diária Guia (R$) - mínimo que a Diária Mínima (Até 4 PAX) pode ter. Mínimo do piso: 250. */
  pisoDiariaGuia?: number;
  /** Premiações globais para ranking geral (mensal/anual: 1º,2º,3º). */
  premiacaoRanking?: PremiacaoConfig;
}

function loadJson<T>(key: string, fallback: T): T {
  try {
    const s = localStorage.getItem(key);
    return s ? (JSON.parse(s) as T) : fallback;
  } catch {
    return fallback;
  }
}

/** Retorna true se salvou com sucesso, false se falhou. Em caso de QuotaExceededError, lança erro com mensagem amigável. */
function saveJson(key: string, data: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    return true;
  } catch (err) {
    console.error('[saveJson] Falha ao salvar no localStorage:', err);
    const isQuota = err instanceof DOMException && (err.name === 'QuotaExceededError' || err.code === 22);
    if (isQuota) {
      throw new Error(
        'Armazenamento local do navegador cheio. Se ainda houver fotos em base64 nas experiências, use em Experiências → Imagens a opção «Migrar fotos antigas para a nuvem»; caso contrário, reduza dados guardados neste site ou limpe o armazenamento do domínio.'
      );
    }
    return false;
  }
}

// ——— Categorias ———
/** Retorna apenas as categorias criadas pelo admin (sem defaults mockados). Sempre retorna array. */
export function getCategorias(): Categoria[] {
  try {
    const data = loadJson<Categoria[]>(KEY_CATEGORIAS, []);
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

export function saveCategorias(list: Categoria[]) {
  saveJson(KEY_CATEGORIAS, list);
}

export function addCategoria(name: string): Categoria {
  const list = getCategorias();
  const id = `cat-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const cat: Categoria = { id, name: name.trim() };
  list.push(cat);
  saveCategorias(list);
  return cat;
}

export function updateCategoria(id: string, name: string): void {
  const list = getCategorias();
  const i = list.findIndex((c) => c.id === id);
  if (i >= 0) {
    list[i] = { ...list[i], name: name.trim() };
    saveCategorias(list);
  }
}

export function removeCategoria(id: string): { ok: boolean; error?: string } {
  const exps = getExperiencias();
  const used = exps.some((e) => e.categoryIds.includes(id));
  if (used) return { ok: false, error: 'Não é possível excluir: há experiências usando esta categoria.' };
  const subs = getSubcategorias();
  const subsOnlyThis = subs.filter((s) => s.categoryId === id);
  if (subsOnlyThis.length > 0) return { ok: false, error: 'Exclua ou reassocie as subcategorias desta categoria antes.' };
  const list = getCategorias().filter((c) => c.id !== id);
  saveCategorias(list);
  saveSubcategorias(subs.filter((s) => s.categoryId !== id));
  return { ok: true };
}

/** Garante que existe a categoria Terapia e suas subcategorias. Retorna o id da categoria Terapia. */
export function ensureTerapiaCategoryAndSubs(): string {
  const cats = getCategorias();
  let terapiaCat = cats.find((c) => (c.name ?? '').toLowerCase().includes('terapia'));
  if (!terapiaCat) {
    terapiaCat = addCategoria('Terapia');
  }
  const subs = getSubcategorias();
  const subNames = ['Destaques', 'Corporal', 'Energética e Espiritual', 'Autoconhecimento'];
  for (const name of subNames) {
    const exists = subs.some((s) => (s.name ?? '').trim() === name && s.categoryId === terapiaCat!.id);
    if (!exists) {
      addSubcategoria(name, terapiaCat!.id);
    }
  }
  return terapiaCat.id;
}

// ——— Subcategorias ———

type SubcategoriaRaw = Subcategoria & { categoryIds?: string[] };

function normalizeSubcategoriasFromStorage(raw: unknown[]): Subcategoria[] {
  const parsed: Subcategoria[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as SubcategoriaRaw;
    const id = typeof o.id === 'string' ? o.id : '';
    const name = typeof o.name === 'string' ? o.name.trim() : '';
    if (!id || !name) continue;
    let categoryId = typeof o.categoryId === 'string' ? o.categoryId : '';
    if (!categoryId && Array.isArray(o.categoryIds) && o.categoryIds.length > 0) {
      categoryId = String(o.categoryIds[0]);
    }
    if (!categoryId) continue;
    const sortOrder = typeof o.sortOrder === 'number' && Number.isFinite(o.sortOrder) ? o.sortOrder : 0;
    parsed.push({ id, name, categoryId, sortOrder });
  }
  const byCat = new Map<string, Subcategoria[]>();
  for (const s of parsed) {
    const arr = byCat.get(s.categoryId) ?? [];
    arr.push(s);
    byCat.set(s.categoryId, arr);
  }
  const out: Subcategoria[] = [];
  for (const [, arr] of byCat) {
    arr.sort((a, b) => (a.sortOrder !== b.sortOrder ? a.sortOrder - b.sortOrder : a.id.localeCompare(b.id)));
    arr.forEach((s, idx) => out.push({ ...s, sortOrder: idx }));
  }
  return out;
}

/** Retorna apenas as subcategorias criadas pelo admin (sem defaults mockados). Sempre retorna array. Migra formato legado (categoryIds). */
export function getSubcategorias(): Subcategoria[] {
  try {
    const raw = loadJson<unknown[]>(KEY_SUBCATEGORIAS, []);
    if (!Array.isArray(raw)) return [];
    const needsMigrate = raw.some((x) => {
      if (!x || typeof x !== 'object') return false;
      const r = x as SubcategoriaRaw;
      return Array.isArray(r.categoryIds) && (!r.categoryId || r.categoryId === '');
    });
    const normalized = normalizeSubcategoriasFromStorage(raw);
    if (needsMigrate && normalized.length >= 0) {
      try {
        saveJson(KEY_SUBCATEGORIAS, normalized);
      } catch {
        // ignore
      }
    }
    return normalized;
  } catch {
    return [];
  }
}

export function saveSubcategorias(list: Subcategoria[]) {
  saveJson(KEY_SUBCATEGORIAS, list);
}

/** Subcategorias de uma categoria, ordenadas para exibição (site e admin). */
export function getSubcategoriasForCategorySorted(categoryId: string): Subcategoria[] {
  return getSubcategorias()
    .filter((s) => s.categoryId === categoryId)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
}

export function getSubcategoriasByCategoryIds(categoryIds: string[]): Subcategoria[] {
  if (categoryIds.length === 0) return [];
  const set = new Set(categoryIds);
  return getSubcategorias()
    .filter((s) => set.has(s.categoryId))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
}

export function addSubcategoria(name: string, categoryId: string): Subcategoria {
  if (!categoryId?.trim()) throw new Error('Subcategoria deve estar associada a uma categoria.');
  const list = getSubcategorias();
  const inCat = list.filter((s) => s.categoryId === categoryId);
  const maxOrder = inCat.reduce((m, s) => Math.max(m, s.sortOrder), -1);
  const id = `sub-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const sub: Subcategoria = { id, name: name.trim(), categoryId, sortOrder: maxOrder + 1 };
  list.push(sub);
  saveSubcategorias(list);
  return sub;
}

export function updateSubcategoria(id: string, name: string, categoryId: string): void {
  if (!categoryId?.trim()) throw new Error('Subcategoria deve estar associada a uma categoria.');
  const list = getSubcategorias();
  const i = list.findIndex((s) => s.id === id);
  if (i < 0) return;
  const old = list[i]!;
  if (old.categoryId === categoryId) {
    list[i] = { ...old, name: name.trim() };
    saveSubcategorias(list);
    return;
  }
  const rest = list.filter((s) => s.id !== id);
  const inNew = rest.filter((s) => s.categoryId === categoryId);
  const maxOrder = inNew.reduce((m, s) => Math.max(m, s.sortOrder), -1);
  rest.push({ ...old, name: name.trim(), categoryId, sortOrder: maxOrder + 1 });
  saveSubcategorias(rest);
}

/** Reordena subcategorias de uma categoria (ids na ordem desejada). */
export function reorderSubcategoriasForCategory(categoryId: string, orderedIds: string[]): void {
  const list = getSubcategorias();
  const inCat = list.filter((s) => s.categoryId === categoryId);
  if (orderedIds.length !== inCat.length) {
    throw new Error('Ordem inválida: quantidade de subcategorias não confere.');
  }
  const idSet = new Set(orderedIds);
  if (!inCat.every((s) => idSet.has(s.id))) {
    throw new Error('Ordem inválida: IDs não pertencem a esta categoria.');
  }
  const others = list.filter((s) => s.categoryId !== categoryId);
  const reordered = orderedIds.map((subId, idx) => {
    const s = list.find((x) => x.id === subId)!;
    return { ...s, sortOrder: idx };
  });
  saveSubcategorias([...others, ...reordered]);
}

export function removeSubcategoria(id: string): { ok: boolean; error?: string } {
  const subs = getSubcategorias();
  const sub = subs.find((s) => s.id === id);
  if (!sub) return { ok: true };
  const othersInCat = subs.filter((s) => s.id !== id && s.categoryId === sub.categoryId);
  if (othersInCat.length === 0) {
    return { ok: false, error: 'Não é possível excluir: uma categoria ficaria sem subcategorias.' };
  }
  const next = subs.filter((s) => s.id !== id);
  const inCat = next.filter((s) => s.categoryId === sub.categoryId).sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id));
  const fixed = inCat.map((s, idx) => ({ ...s, sortOrder: idx }));
  const rest = next.filter((s) => s.categoryId !== sub.categoryId);
  saveSubcategorias([...rest, ...fixed]);
  return { ok: true };
}

// ——— Tags só de Experiências (`naviex-admin-tags`). Estabelecimentos usam `estabelecimentosTagsStorage`. ———
const KEY_TAGS = 'naviex-admin-tags';
const DEFAULT_TAGS = ['Cachoeira', 'Praia de rio', 'Formações Rochosas', 'Rio', 'Céu'];

export function getTags(): string[] {
  try {
    const data = loadJson<string[]>(KEY_TAGS, DEFAULT_TAGS);
    return Array.isArray(data) ? data : DEFAULT_TAGS;
  } catch {
    return DEFAULT_TAGS;
  }
}

function saveTags(list: string[]): void {
  saveJson(KEY_TAGS, list);
}

/** Adiciona uma tag à lista global se ainda não existir (case-sensitive, trim). */
export function addTag(name: string): void {
  const t = name.trim();
  if (!t) return;
  const list = getTags();
  if (list.includes(t)) return;
  list.push(t);
  list.sort((a, b) => a.localeCompare(b));
  saveTags(list);
}

/** Renomeia tag na lista global e em todas as experiências que a usam. */
export function renameTag(oldName: string, newName: string): void {
  const t = newName.trim();
  if (!t || t === oldName) return;
  const list = getTags().filter((x) => x !== oldName);
  if (!list.includes(t)) list.push(t);
  list.sort((a, b) => a.localeCompare(b));
  saveTags(list);
  const exps = getExperiencias();
  exps.forEach((exp) => {
    if ((exp.tags ?? []).includes(oldName)) {
      saveExperiencia({
        ...exp,
        tags: (exp.tags ?? []).map((tag) => (tag === oldName ? t : tag)),
      });
    }
  });
}

/** Remove tag apenas da lista global (use quando a tag não está em nenhuma experiência). */
export function removeTagFromList(name: string): void {
  saveTags(getTags().filter((x) => x !== name));
}

/** Remove tag da lista global e de todas as experiências. */
export function removeTagCompletely(name: string): void {
  removeTagFromList(name);
  getExperiencias().forEach((exp) => {
    if ((exp.tags ?? []).includes(name)) {
      saveExperiencia({
        ...exp,
        tags: (exp.tags ?? []).filter((t) => t !== name),
      });
    }
  });
}

// ——— Experiências ———
export function getExperiencias(): ExperienciaAdmin[] {
  return loadJson<ExperienciaAdmin[]>(KEY_EXPERIENCIAS, []);
}

export function getExperienciaById(id: string): ExperienciaAdmin | null {
  return getExperiencias().find((e) => e.id === id) ?? null;
}

/**
 * Retorna o status efetivo da experiência em uma data (YYYY-MM-DD).
 * Se a data estiver dentro de algum período em periodosStatus, usa o tipo desse período;
 * caso contrário, usa statusPadrao (default 'ativada').
 */
export function getExperienciaStatusEfetivo(
  exp: { statusPadrao?: ExperienciaStatusPadraoValor | string; periodosStatus?: { de: string; ate: string; tipo: string }[] },
  data?: string
): 'ativada' | 'desativada' {
  const hoje = data ?? ymdReferenciaAgendaStatusLocal();
  const tipoDom = tipoStatusProgramadoDominanteNoDia(exp.periodosStatus, hoje);
  if (tipoDom) {
    if (tipoDom === 'desativada' || tipoDom === 'desativado') return 'desativada';
    return 'ativada';
  }
  return experienciaStatusPadraoContaComoAtiva(exp.statusPadrao) ? 'ativada' : 'desativada';
}

/** True se a experiência estiver efetivamente ativa na data (considerando status programado). */
export function isExperienciaAtiva(
  exp: { statusPadrao?: ExperienciaStatusPadraoValor | string; periodosStatus?: { de: string; ate: string; tipo: string }[] },
  data?: string
): boolean {
  return getExperienciaStatusEfetivo(exp, data) === 'ativada';
}

/** Retorna apenas experiências publicadas e efetivamente ativas (status programado aplicado na data de hoje). */
export function getPublishedExperiences(): ExperienciaAdmin[] {
  return getExperiencias().filter((e) => e.status === 'publicado' && isExperienciaAtiva(e));
}

export function saveExperiencia(exp: ExperienciaAdmin): void {
  const list = getExperiencias();
  const i = list.findIndex((e) => e.id === exp.id);
  const now = new Date().toISOString();
  const toSave = { ...exp, updatedAt: now };
  if (i >= 0) list[i] = toSave;
  else list.push({ ...toSave, createdAt: now } as ExperienciaAdmin);
  if (!saveJson(KEY_EXPERIENCIAS, list)) {
    throw new Error('Não foi possível salvar a experiência. Verifique armazenamento do navegador.');
  }
}

/**
 * Sincroniza relacionados de forma bidirecional: para cada experiência B presente nos
 * relacionados de A, adiciona A aos relacionados de B (e das demais do combo).
 * Remove de B os rows que continham A mas não existem mais em A.
 */
export function syncRelacionadosBidirecional(
  expId: string,
  novosRelacionados: RelacionadoRow[],
  antigosRelacionados?: RelacionadoRow[]
): void {
  const list = getExperiencias();
  const relatedIdsNovos = new Set<string>();
  const mirrorRowsByRelated: Record<string, RelacionadoRow[]> = {};

  for (const row of novosRelacionados) {
    const ids = row.experienciaIds.filter(Boolean);
    for (const rid of ids) {
      relatedIdsNovos.add(rid);
      const outrosNoCombo = ids.filter((id) => id !== rid);
      const mirrorIds = [expId, ...outrosNoCombo];
      if (!mirrorRowsByRelated[rid]) mirrorRowsByRelated[rid] = [];
      mirrorRowsByRelated[rid].push({
        experienciaIds: mirrorIds,
        tarifarioId: row.tarifarioId,
        duracaoPasseio: row.duracaoPasseio,
        cidadesEmbarqueIds: row.cidadesEmbarqueIds,
      });
    }
  }

  const relatedIdsAntigos = new Set<string>();
  for (const row of antigosRelacionados ?? []) {
    for (const rid of row.experienciaIds.filter(Boolean)) {
      relatedIdsAntigos.add(rid);
    }
  }

  const todosAfetados = new Set([...relatedIdsNovos, ...relatedIdsAntigos]);

  for (const relatedId of todosAfetados) {
    const related = list.find((e) => e.id === relatedId);
    if (!related) continue;
    const novosRows = mirrorRowsByRelated[relatedId] ?? [];
    const outrosRows = (related.relacionados ?? []).filter(
      (r) => !r.experienciaIds.includes(expId)
    );
    const updated = [...outrosRows, ...novosRows];
    const i = list.findIndex((e) => e.id === relatedId);
    if (i >= 0) {
      list[i] = { ...related, relacionados: updated, updatedAt: new Date().toISOString() };
    }
  }
  saveJson(KEY_EXPERIENCIAS, list);
}

export function addExperiencia(exp: Omit<ExperienciaAdmin, 'id' | 'createdAt' | 'updatedAt'>): ExperienciaAdmin {
  const now = new Date().toISOString();
  const id = `exp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  const full: ExperienciaAdmin = {
    ...exp,
    id,
    createdAt: now,
    updatedAt: now,
    status: exp.status ?? 'rascunho',
  };
  const list = getExperiencias();
  list.push(full);
  if (!saveJson(KEY_EXPERIENCIAS, list)) {
    throw new Error('Não foi possível salvar a experiência. Verifique armazenamento do navegador.');
  }
  return full;
}

export function removeExperiencia(id: string): void {
  const list = getExperiencias()
    .filter((e) => e.id !== id)
    .map((e) => ({
      ...e,
      relacionados: (e.relacionados ?? []).filter((r) => !r.experienciaIds.includes(id)),
    }));
  saveJson(KEY_EXPERIENCIAS, list);
}

/**
 * Repara todas as relações bidirecionais: para cada experiência A que tem B em relacionados,
 * garante que B também tenha A (ou o combo equivalente). Corrige dados antigos que foram
 * salvos antes da sincronização bidirecional.
 */
export function repararRelacionadosBidirecionais(): void {
  const list = getExperiencias();
  let changed = false;

  for (const exp of list) {
    const rows = exp.relacionados ?? [];
    if (rows.length === 0) continue;

    const mirrorByRelated: Record<string, RelacionadoRow[]> = {};
    for (const row of rows) {
      const ids = row.experienciaIds.filter(Boolean);
      for (const relatedId of ids) {
        if (!mirrorByRelated[relatedId]) mirrorByRelated[relatedId] = [];
        const outrosNoCombo = ids.filter((id) => id !== relatedId);
        const mirror: RelacionadoRow = {
          experienciaIds: [exp.id, ...outrosNoCombo],
          tarifarioId: row.tarifarioId,
          duracaoPasseio: row.duracaoPasseio,
          cidadesEmbarqueIds: row.cidadesEmbarqueIds,
        };
        const mirrorKey = [...mirror.experienciaIds].sort().join(',');
        if (!mirrorByRelated[relatedId].some((m) => [...m.experienciaIds].sort().join(',') === mirrorKey)) {
          mirrorByRelated[relatedId].push(mirror);
        }
      }
    }

    for (const [relatedId, mirrors] of Object.entries(mirrorByRelated)) {
      const i = list.findIndex((e) => e.id === relatedId);
      if (i < 0) continue;
      const related = list[i];
      const outrosRows = (related.relacionados ?? []).filter((r) => !r.experienciaIds.includes(exp.id));
      const updated = [...outrosRows, ...mirrors];
      const orig = related.relacionados ?? [];
      const newKeys = new Set(updated.map((r) => [...r.experienciaIds].sort().join(',')));
      const oldKeys = new Set(orig.map((r) => [...r.experienciaIds].sort().join(',')));
      const changedHere = newKeys.size !== oldKeys.size || [...newKeys].some((k) => !oldKeys.has(k));
      if (changedHere) {
        list[i] = { ...related, relacionados: updated, updatedAt: new Date().toISOString() };
        changed = true;
      }
    }
  }

  if (changed) {
    saveJson(KEY_EXPERIENCIAS, list);
  }
}

/** Experiências cadastradas na mesma região da cidade informada (para "Faça no mesmo dia"). Exclui a experiência com excludeExperienciaId.
 * Se a cidade não tem regiões configuradas, retorna experiências da mesma cidade (cidadeId). */
export function getExperienciasNaMesmaRegiao(cidadeId: string, excludeExperienciaId?: string): ExperienciaAdmin[] {
  const cidade = getCidadeById(cidadeId);
  if (!cidade) return [];
  const idsCidades: Set<string> = cidade.regioes?.length
    ? new Set(getCidadesByRegioes(cidade.regioes).map((c) => c.id))
    : new Set([cidade.id]);
  return getExperiencias().filter(
    (e) => e.cidadeId && idsCidades.has(e.cidadeId) && e.id !== excludeExperienciaId
  );
}

// ——— Cidades ———
function normalizeCidade(c: Cidade & { regiao?: string }): Cidade {
  const regioes = Array.isArray(c.regioes) ? c.regioes : (c.regiao ? [c.regiao] : []);
  return { ...c, regioes };
}

/** Hash numérico estável do id (igual ao do cliente para compatibilidade). */
function hashId(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h) || 1;
}

/** Mapeia categoria/subcategoria por nome para o formato do catálogo (cliente). */
function mapCategoryForExport(
  categoryIds: string[],
  subcategoryIds: string[],
  categorias: Categoria[],
  subcategorias: Subcategoria[]
): { category: string; natureTourCategory?: string; therapyCategory?: string } {
  const catId = categoryIds?.[0];
  const subId = subcategoryIds?.[0];
  const categoria = catId ? categorias.find((c) => c.id === catId) : null;
  const subcategoria = subId ? subcategorias.find((s) => s.id === subId) : null;
  const catName = (categoria?.name ?? '').toLowerCase().trim();
  const subName = (subcategoria?.name ?? '').toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (catName.includes('natureza')) {
    if (subName.includes('classico') || subName.includes('clássico')) return { category: 'nature_tour', natureTourCategory: 'classic' };
    if (subName.includes('lado b')) return { category: 'nature_tour', natureTourCategory: 'side_b' };
    if (subName.includes('destaque')) return { category: 'nature_tour', natureTourCategory: 'highlights' };
    if (subName.includes('familia') || subName.includes('família')) return { category: 'nature_tour', natureTourCategory: 'family' };
    if (subName.includes('noturno')) return { category: 'nature_tour', natureTourCategory: 'night' };
    return { category: 'nature_tour', natureTourCategory: 'classic' };
  }
  if (catName.includes('terapia')) {
    if (subName.includes('corporal')) return { category: 'therapy', therapyCategory: 'physical' };
    if (subName.includes('espiritual') || subName.includes('energetica') || subName.includes('energética')) return { category: 'therapy', therapyCategory: 'spiritual_energetic' };
    if (subName.includes('autoconhecimento') || subName.includes('emocional')) return { category: 'therapy', therapyCategory: 'self_knowledge_emotional' };
    return { category: 'therapy', therapyCategory: 'physical' };
  }
  if (catName.includes('aventura')) return { category: 'adventure' };
  if (catName.includes('city')) return { category: 'city_tour' };
  return { category: 'nature_tour', natureTourCategory: 'classic' };
}

function collectNatureTourCategoriesForExport(
  categoryIds: string[],
  subcategoryIds: string[],
  categorias: Categoria[],
  subcategorias: Subcategoria[]
): string[] | undefined {
  const catId = categoryIds?.[0];
  const categoria = catId ? categorias.find((c) => c.id === catId) : null;
  const catName = (categoria?.name ?? '').toLowerCase().trim();
  if (!catName.includes('natureza')) return undefined;
  const set = new Set<string>();
  if (!subcategoryIds?.length) {
    const m = mapCategoryForExport(categoryIds, [], categorias, subcategorias);
    if (m.natureTourCategory) set.add(m.natureTourCategory);
  } else {
    for (const sid of subcategoryIds) {
      const m = mapCategoryForExport(categoryIds, [sid], categorias, subcategorias);
      if (m.natureTourCategory) set.add(m.natureTourCategory);
    }
  }
  return set.size > 0 ? [...set] : undefined;
}

function collectTherapyCategoriesForExport(
  categoryIds: string[],
  subcategoryIds: string[],
  categorias: Categoria[],
  subcategorias: Subcategoria[]
): string[] | undefined {
  const catId = categoryIds?.[0];
  const categoria = catId ? categorias.find((c) => c.id === catId) : null;
  const catName = (categoria?.name ?? '').toLowerCase().trim();
  if (!catName.includes('terapia')) return undefined;
  const set = new Set<string>();
  if (!subcategoryIds?.length) {
    const m = mapCategoryForExport(categoryIds, [], categorias, subcategorias);
    if (m.therapyCategory) set.add(m.therapyCategory);
  } else {
    for (const sid of subcategoryIds) {
      const m = mapCategoryForExport(categoryIds, [sid], categorias, subcategorias);
      if (m.therapyCategory) set.add(m.therapyCategory);
    }
  }
  return set.size > 0 ? [...set] : undefined;
}

/** Valores de transporte por tarifário (Valor Plataforma BRL) para cálculo Exclusivo SUV/Compacto. */
export function getTarifariosTransporteForExport(): Record<string, { excursaoIndividualTranslado: number; veiculo4x4_5: number; veiculo4x4_7: number; veiculoPasseio: number; deslocamentoMoto: number }> {
  const out: Record<string, { excursaoIndividualTranslado: number; veiculo4x4_5: number; veiculo4x4_7: number; veiculoPasseio: number; deslocamentoMoto: number }> = {};
  getTarifarios().forEach((t) => {
    const exc = t.excursaoIndividualTranslado?.brl;
    const v5 = t.veiculo4x4_4?.brl;
    const v7 = t.veiculo4x4_6?.brl;
    const vp = t.veiculoPasseio?.brl;
    const dm = t.deslocamentoMoto?.brl;
    out[t.id] = {
      excursaoIndividualTranslado: typeof exc === 'number' && Number.isFinite(exc) ? Math.round(exc) : 0,
      veiculo4x4_5: typeof v5 === 'number' && Number.isFinite(v5) ? Math.round(v5) : 0,
      veiculo4x4_7: typeof v7 === 'number' && Number.isFinite(v7) ? Math.round(v7) : 0,
      veiculoPasseio: typeof vp === 'number' && Number.isFinite(vp) ? Math.round(vp) : 0,
      deslocamentoMoto: typeof dm === 'number' && Number.isFinite(dm) ? Math.round(dm) : 0,
    };
  });
  return out;
}

/** Tarifário resumido para catálogo: id + preço individual por PAX (1..12) em R$ + descontos + Individual PAX 5 Desconto Grupo + aditivo guia bilíngue. */
export function getTarifariosForCatalogExport(): Array<{
  id: string;
  individualPorPax: number[];
  descontoIdoso?: number;
  descontoAdolescenteCrianca?: number;
  /** Individual PAX 5 Desconto Grupo (R$) - usado para PAX >= 4 em natureza/cidade/aventura */
  excursaoIndividualBrl?: number;
  /** Aditivo guia bilíngue (%) - usado no checkout para acréscimo por dia */
  aditivoGuiaBilingue?: number;
}> {
  return getTarifarios().map((t) => {
    const arr = (t.passeioExclusivo ?? []).slice(0, 12);
    const excursaoBrl = t.excursaoIndividual?.brl;
    const individualPorPax = Array.from({ length: 12 }, (_, i) => {
      if (i < 4) {
        const v = arr[i]?.brl;
        return typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : 0;
      }
      return typeof excursaoBrl === 'number' && Number.isFinite(excursaoBrl) ? Math.round(excursaoBrl) : 0;
    });
    return {
      id: t.id,
      individualPorPax,
      descontoIdoso: t.descontoIdoso,
      descontoAdolescenteCrianca: t.descontoAdolescenteCrianca,
      excursaoIndividualBrl: typeof excursaoBrl === 'number' && Number.isFinite(excursaoBrl) ? Math.round(excursaoBrl) : undefined,
      aditivoGuiaBilingue: typeof t.aditivoGuiaBilingue === 'number' && Number.isFinite(t.aditivoGuiaBilingue) ? t.aditivoGuiaBilingue : undefined,
    };
  });
}

/** Experiências publicadas no formato de cards para o cliente (catálogo). */
export function getPublishedExperienceCardsForExport(): Array<{
  id: number;
  title: string;
  subtitle?: string;
  imageUrl: string;
  pessoas: string;
  preco: string;
  city?: string;
  embarkationCities?: string[];
  /** Por cidade de embarque: nome da cidade e tarifário associado (para calcular preço por PAX). */
  embarkationTariffIds?: { cityName: string; tarifarioId: string }[];
  category: string;
  natureTourCategory?: string;
  natureTourCategories?: string[];
  therapyCategory?: string;
  therapyCategories?: string[];
  experienceId: string;
  adminSubcategoryIds?: string[];
  relatedExperienceIds?: string[];
  /** Tarifário com aditivo guia bilíngue > 0 */
  bilingual?: boolean;
}> {
  const exps = getExperiencias();
  const categorias = getCategorias();
  const subcategorias = getSubcategorias();
  const published = exps.filter(
    (e) => e.status === 'publicado' && isExperienciaAtiva(e) && !e.naoEhAtividadePrincipal
  );
  const publishedIds = new Set(
    exps.filter((e) => e.status === 'publicado' && isExperienciaAtiva(e)).map((e) => e.id)
  );
  return published.map((e) => {
    const { category, natureTourCategory, therapyCategory } = mapCategoryForExport(
      e.categoryIds ?? [],
      e.subcategoryIds ?? [],
      categorias,
      subcategorias
    );
    const embarkationCities: string[] = [];
    const embarkationTariffIds: { cityName: string; tarifarioId: string }[] = [];
    e.cidadesEmbarque?.forEach((ce) => {
      const nome = (ce.cidadeNome?.trim() || getCidadeById(ce.cidadeId)?.cidade?.trim()) ?? '';
      if (nome && !embarkationCities.includes(nome)) embarkationCities.push(nome);
      if (ce.tarifarioId && nome) embarkationTariffIds.push({ cityName: nome, tarifarioId: ce.tarifarioId });
    });
    const relatedExperienceIds = Array.from(
      new Set(
        (e.relacionados ?? [])
          .flatMap((r) => r.experienciaIds ?? [])
          .filter((id) => id && id !== e.id && publishedIds.has(id))
      )
    );
    const rawSubs = (e.subcategoryIds ?? []).map((id) => String(id).trim()).filter(Boolean);
    let bilingual = false;
    for (const row of embarkationTariffIds) {
      const t = getTarifarioById(row.tarifarioId);
      if (t && typeof t.aditivoGuiaBilingue === 'number' && t.aditivoGuiaBilingue > 0) {
        bilingual = true;
        break;
      }
    }
    return {
      id: hashId(e.id),
      bilingual: bilingual || undefined,
      title: e.titulo || '',
      subtitle: e.subtitulo,
      imageUrl: e.imagens?.[0] || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80',
      pessoas: 'Consultar',
      preco: 'A partir de R$ —',
      city: e.cidadeNome,
      embarkationCities: embarkationCities.length > 0 ? embarkationCities : undefined,
      embarkationTariffIds: embarkationTariffIds.length > 0 ? embarkationTariffIds : undefined,
      category,
      natureTourCategory,
      natureTourCategories: collectNatureTourCategoriesForExport(
        e.categoryIds ?? [],
        e.subcategoryIds ?? [],
        categorias,
        subcategorias
      ),
      therapyCategory,
      therapyCategories: collectTherapyCategoriesForExport(
        e.categoryIds ?? [],
        e.subcategoryIds ?? [],
        categorias,
        subcategorias
      ),
      experienceId: e.id,
      adminSubcategoryIds: rawSubs.length > 0 ? rawSubs : undefined,
      relatedExperienceIds: relatedExperienceIds.length > 0 ? relatedExperienceIds : undefined,
    };
  });
}

/**
 * Lista de destinos para o cliente ("Bora pra onde?"): somente cidades de embarque de experiências
 * publicadas e ativas. Não inclui o catálogo inteiro de cidades nem a cidade da experiência quando
 * não há ponto de embarque nela (ex.: atividade em São João d'Aliança com embarque só em Alto Paraíso).
 */
export function getDestinosForExport(): { name: string; regions: string[]; estadoProvincia?: string; imageUrl?: string }[] {
  const cidades = getCidades();
  const namesSet = new Set<string>();
  const exps = getExperiencias();
  const published = exps.filter((e) => e.status === 'publicado' && isExperienciaAtiva(e));
  published.forEach((e) => {
    e.cidadesEmbarque?.forEach((ce) => {
      const nome = ce.cidadeNome?.trim() || getCidadeById(ce.cidadeId)?.cidade?.trim();
      if (nome) namesSet.add(nome);
    });
  });
  return Array.from(namesSet).map((name) => {
    const cidade = cidades.find((c) => c.cidade.trim() === name);
    let imageUrl: string | undefined;
    if (cidade?.fotos?.length) {
      const idx = Math.min(cidade.fotoPrincipalIndex ?? 0, cidade.fotos.length - 1);
      imageUrl = cidade.fotos[idx];
    }
    const estadoProvincia = (cidade?.estadoProvincia ?? '').trim() || undefined;
    return { name, regions: cidade?.regioes ?? [], estadoProvincia, imageUrl };
  });
}

/** Lista bruta do storage (pode conter cidades duplicadas com ids diferentes). */
function loadCidadesRaw(): Cidade[] {
  try {
    const data = loadJson<(Cidade & { regiao?: string })[]>(KEY_CIDADES, []);
    return (Array.isArray(data) ? data : []).map(normalizeCidade);
  } catch {
    return [];
  }
}

function stripDiacriticsCidade(s: string): string {
  return (s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/** Chave estável para fundir duplicatas (mesmo município no mesmo estado e CEP). */
function cidadeIdentityKey(c: Cidade): string {
  const nome = stripDiacriticsCidade((c.cidade ?? '').trim().toLowerCase());
  const uf = stripDiacriticsCidade((c.estadoProvincia ?? '').trim().toLowerCase());
  const cep = (c.cep ?? '').replace(/\D/g, '');
  return `${nome}|${uf}|${cep}`;
}

function canonicalIdForCidadeKey(key: string, raw: Cidade[]): string {
  const matches = raw.filter((c) => cidadeIdentityKey(c) === key);
  if (matches.length === 0) return '';
  return matches.map((m) => m.id).sort()[0];
}

function dedupeCidadesForDisplay(raw: Cidade[]): Cidade[] {
  const orderedKeys: string[] = [];
  const seen = new Set<string>();
  for (const c of raw) {
    const k = cidadeIdentityKey(c);
    if (!seen.has(k)) {
      seen.add(k);
      orderedKeys.push(k);
    }
  }
  return orderedKeys.map((k) => {
    const canonId = canonicalIdForCidadeKey(k, raw);
    const row = raw.find((r) => r.id === canonId)!;
    return normalizeCidade(row);
  });
}

/** Retorna cidades sem duplicar o mesmo município+estado+CEP (uma linha por identidade). */
export function getCidades(): Cidade[] {
  return dedupeCidadesForDisplay(loadCidadesRaw());
}

/** Id canónico quando há registos duplicados no storage com ids diferentes. */
export function resolveCidadeId(id: string): string {
  const t = (id ?? '').trim();
  if (!t) return id;
  const raw = loadCidadesRaw();
  const hit = raw.find((c) => c.id === t);
  if (!hit) return id;
  return canonicalIdForCidadeKey(cidadeIdentityKey(hit), raw) || id;
}

export function getCidadeById(id: string): Cidade | null {
  const raw = loadCidadesRaw();
  const hit = raw.find((c) => c.id === id);
  if (!hit) return null;
  const canonId = canonicalIdForCidadeKey(cidadeIdentityKey(hit), raw);
  const row = raw.find((r) => r.id === canonId);
  return row ? normalizeCidade(row) : null;
}

/** Retorna cidade por nome (cidade), com match flexível (case-insensitive, trim). */
export function getCidadeByNome(cidadeNome: string): Cidade | null {
  const q = (cidadeNome ?? '').trim().toLowerCase();
  if (!q) return null;
  const raw = loadCidadesRaw();
  const hit = raw.find((c) => (c.cidade ?? '').trim().toLowerCase() === q);
  if (!hit) return null;
  return getCidadeById(hit.id);
}

/** Mapeamento conhecido cidade+estado → região (fallback quando cidade não tem regioes no cadastro). */
const CIDADE_REGIAO_MAP: Array<{ cidades: string[]; estado: string; regiao: string }> = [
  {
    cidades: ['alto paraíso de goiás', 'alto paraíso', 'cavalcante', 'são jorge', 'sao jorge'],
    estado: 'Goiás',
    regiao: 'Chapada dos Veadeiros',
  },
];

/** Retorna a região da cidade. Usa regioes do cadastro ou fallback para cidades conhecidas. */
export function getRegiaoFromCidade(cidade: Cidade | null): string {
  if (!cidade) return '';
  const reg = cidade.regioes?.[0]?.trim();
  if (reg) return reg;
  const nomeNorm = (cidade.cidade ?? '').trim().toLowerCase();
  const estadoNorm = (cidade.estadoProvincia ?? '').trim();
  for (const m of CIDADE_REGIAO_MAP) {
    if (m.estado === estadoNorm && m.cidades.some((c) => nomeNorm.includes(c) || c.includes(nomeNorm))) {
      return m.regiao;
    }
  }
  return '';
}

/** Regiões sugeridas para cidade+estado conhecidos (quando cadastro não tem regioes). */
export function getRegioesSugeridasConhecidas(cidade: string, estadoProvincia: string): string[] {
  const nomeNorm = (cidade ?? '').trim().toLowerCase();
  const estadoNorm = (estadoProvincia ?? '').trim();
  for (const m of CIDADE_REGIAO_MAP) {
    if (m.estado === estadoNorm && m.cidades.some((c) => nomeNorm.includes(c) || c.includes(nomeNorm))) {
      return [m.regiao];
    }
  }
  return [];
}

export function updateCidade(id: string, data: Partial<Omit<Cidade, 'id'>>): void {
  const list = loadCidadesRaw();
  const i = list.findIndex((c) => c.id === id);
  if (i >= 0) {
    list[i] = { ...list[i], ...data };
    saveJson(KEY_CIDADES, list);
  }
}

function rawIdsSameCidadeIdentity(id: string, raw: Cidade[]): Set<string> {
  const hit = raw.find((c) => c.id === id);
  if (!hit) return new Set([id]);
  const k = cidadeIdentityKey(hit);
  return new Set(raw.filter((c) => cidadeIdentityKey(c) === k).map((c) => c.id));
}

export function removeCidade(id: string): { ok: boolean; error?: string } {
  const raw = loadCidadesRaw();
  const sameIds = rawIdsSameCidadeIdentity(id, raw);
  const exps = getExperiencias();
  if (exps.some((e) => e.cidadeId && sameIds.has(e.cidadeId)))
    return { ok: false, error: 'Não é possível excluir: há experiências usando esta cidade (localização).' };
  if (exps.some((e) => e.cidadesEmbarque?.some((ce) => ce.cidadeId && sameIds.has(ce.cidadeId))))
    return { ok: false, error: 'Não é possível excluir: há experiências com esta cidade de embarque.' };
  const list = raw.filter((c) => !sameIds.has(c.id));
  saveJson(KEY_CIDADES, list);
  return { ok: true };
}

export function searchCidades(query: string): Cidade[] {
  const q = query.trim().toLowerCase();
  let all: Cidade[];
  try {
    all = getCidades();
  } catch {
    all = [];
  }
  if (!q) return all;
  return all.filter(
    (c) =>
      c.cidade.toLowerCase().includes(q) ||
      c.estadoProvincia.toLowerCase().includes(q) ||
      c.pais.toLowerCase().includes(q) ||
      c.regioes.some((r) => r.toLowerCase().includes(q)) ||
      c.cep.includes(q)
  );
}

/** Distância em km (Haversine) */
function distanciaKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/** Cidades ordenadas por proximidade ao ponto (lat, lng). Só inclui cidades com coordenadas. Opcionalmente filtra por query. */
export function getCidadesProximas(lat: number, lng: number, query?: string): Cidade[] {
  const all = getCidades().filter((c) => Number.isFinite(c.lat) && Number.isFinite(c.lng));
  const q = query?.trim().toLowerCase();
  let filtered = all;
  if (q) {
    filtered = all.filter(
      (c) =>
        c.cidade.toLowerCase().includes(q) ||
        c.estadoProvincia.toLowerCase().includes(q) ||
        c.pais.toLowerCase().includes(q) ||
        c.regioes.some((r) => r.toLowerCase().includes(q))
    );
  }
  const withDist = filtered.map((c) => ({ cidade: c, dist: distanciaKm(lat, lng, c.lat!, c.lng!) }));
  return withDist.sort((a, b) => a.dist - b.dist).slice(0, 50).map((x) => x.cidade);
}

export function getCidadesByRegioes(regioes: string[]): Cidade[] {
  if (regioes.length === 0) return [];
  const all = getCidades();
  return all.filter((c) => c.regioes.some((r) => regioes.includes(r)));
}

export function addCidade(cidade: Omit<Cidade, 'id'>): Cidade {
  const list = loadCidadesRaw();
  const id = `cid-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const c: Cidade = { ...cidade, id, regioes: cidade.regioes ?? [] };
  list.push(c);
  saveJson(KEY_CIDADES, list);
  return c;
}

export function saveCidades(list: Cidade[]): void {
  saveJson(KEY_CIDADES, list.map(normalizeCidade));
}

// ——— Regiões (associadas a um ou mais estados) ———
function getRegioesRaw(): Regiao[] {
  const data = loadJson<Regiao[]>(KEY_REGIOES, []);
  return Array.isArray(data) ? data : [];
}

export function getRegioes(): Regiao[] {
  return getRegioesRaw();
}

/** Regiões disponíveis para o estado: das cidades já cadastradas nesse estado + regiões cadastradas vinculadas ao estado. */
export function getRegioesByEstado(estadoProvincia: string): string[] {
  const estado = estadoProvincia.trim();
  if (!estado) return [];
  const fromCidades = getCidades()
    .filter((c) => (c.estadoProvincia || '').trim() === estado)
    .flatMap((c) => c.regioes ?? []);
  const fromRegioes = getRegioesRaw()
    .filter((r) => r.estados?.includes(estado))
    .map((r) => r.nome);
  return [...new Set([...fromCidades, ...fromRegioes])].sort();
}

/** Adiciona uma nova região ao estado ou associa estado a região existente. */
export function addOrUpdateRegiao(nome: string, estadoProvincia: string): void {
  const nomeNorm = nome.trim();
  const estado = estadoProvincia.trim();
  if (!nomeNorm || !estado) return;
  const list = getRegioesRaw();
  const idx = list.findIndex((r) => r.nome.toLowerCase() === nomeNorm.toLowerCase());
  if (idx >= 0) {
    if (!list[idx].estados.includes(estado)) {
      list[idx] = { ...list[idx], estados: [...list[idx].estados, estado] };
      saveJson(KEY_REGIOES, list);
    }
  } else {
    list.push({ nome: nomeNorm, estados: [estado] });
    saveJson(KEY_REGIOES, list);
  }
}

/** Remove uma região globalmente e desvincula das cidades. */
export function removeRegiao(nome: string): void {
  const nomeNorm = nome.trim().toLowerCase();
  if (!nomeNorm) return;
  const regioes = getRegioesRaw().filter((r) => r.nome.trim().toLowerCase() !== nomeNorm);
  saveJson(KEY_REGIOES, regioes);

  const cidades = loadCidadesRaw().map((c) => ({
    ...c,
    regioes: (c.regioes ?? []).filter((r) => r.trim().toLowerCase() !== nomeNorm),
  }));
  saveJson(KEY_CIDADES, cidades);
}

// ——— Tarifários ———
const defaultTarifarios: Tarifario[] = [];

function getTarifariosRaw(): Tarifario[] {
  const data = loadJson<Tarifario[]>(KEY_TARIFARIOS, []);
  return Array.isArray(data) ? data : [];
}

/** Nome da tarifa: ordem alfabética (pt-BR) e numérica natural (ex. "2" antes de "10"). */
function sortTarifariosByNome(list: Tarifario[]): Tarifario[] {
  return [...list].sort((a, b) =>
    (a.nome ?? '')
      .trim()
      .localeCompare((b.nome ?? '').trim(), 'pt-BR', { numeric: true, sensitivity: 'base' })
  );
}

export function getTarifarios(): Tarifario[] {
  const stored = getTarifariosRaw();
  const missing = defaultTarifarios.filter((d) => !stored.some((s) => s.id === d.id));
  return sortTarifariosByNome([...stored, ...missing]);
}

export function getTarifarioById(id: string): Tarifario | null {
  return getTarifarios().find((t) => t.id === id) ?? null;
}

export function addTarifario(data: Omit<Tarifario, 'id'>): Tarifario {
  const list = getTarifarios();
  const id = `tar-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const t: Tarifario = { ...data, id };
  list.push(t);
  saveJson(KEY_TARIFARIOS, list);
  return t;
}

export function updateTarifario(id: string, data: Partial<Omit<Tarifario, 'id'>>): void {
  const list = getTarifarios();
  const i = list.findIndex((t) => t.id === id);
  if (i >= 0) {
    list[i] = { ...list[i], ...data };
    saveJson(KEY_TARIFARIOS, list);
  }
}

export function removeTarifario(id: string): { ok: boolean; error?: string } {
  const exps = getExperiencias();
  if (exps.some((e) => e.cidadesEmbarque?.some((ce) => ce.tarifarioId === id)))
    return { ok: false, error: 'Não é possível excluir: há experiências usando este tarifário.' };
  const list = getTarifarios().filter((t) => t.id !== id);
  saveJson(KEY_TARIFARIOS, list);
  return { ok: true };
}

// ——— Configurações globais do tarifário ———
export function getTarifarioGlobalConfig(): TarifarioGlobalConfig {
  const data = loadJson<TarifarioGlobalConfig>(KEY_TARIFARIO_GLOBAL, {});
  return data && typeof data === 'object' ? data : {};
}

export function saveTarifarioGlobalConfig(config: TarifarioGlobalConfig): void {
  if (!saveJson(KEY_TARIFARIO_GLOBAL, config)) {
    throw new Error('Não foi possível salvar as configurações globais.');
  }
}

// ——— Pontos de Encontro ———
function getPontosEncontroRaw(): PontoEncontro[] {
  const data = loadJson<PontoEncontro[]>(KEY_PONTOS_ENCONTRO, []);
  return Array.isArray(data) ? data : [];
}

export function getPontosEncontro(cidadeId?: string): PontoEncontro[] {
  const all = getPontosEncontroRaw();
  if (cidadeId) return all.filter((p) => p.cidadeId === cidadeId);
  return all;
}

export function getPontoEncontroById(id: string): PontoEncontro | null {
  return getPontosEncontroRaw().find((p) => p.id === id) ?? null;
}

export function getPontoEncontroPadrao(cidadeId: string): PontoEncontro | null {
  const daCidade = getPontosEncontro(cidadeId);
  return daCidade.find((p) => p.padrao) ?? daCidade[0] ?? null;
}

export function addPontoEncontro(data: Omit<PontoEncontro, 'id'>): PontoEncontro {
  const list = getPontosEncontroRaw();
  const id = `pto-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  const p: PontoEncontro = { ...data, id };
  list.push(p);
  saveJson(KEY_PONTOS_ENCONTRO, list);
  return p;
}

export function updatePontoEncontro(id: string, data: Partial<Omit<PontoEncontro, 'id'>>): void {
  const list = getPontosEncontroRaw();
  const i = list.findIndex((p) => p.id === id);
  if (i >= 0) {
    list[i] = { ...list[i], ...data };
    saveJson(KEY_PONTOS_ENCONTRO, list);
  }
}

export function removePontoEncontro(id: string): void {
  const list = getPontosEncontroRaw().filter((p) => p.id !== id);
  saveJson(KEY_PONTOS_ENCONTRO, list);
}
